import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import * as Hub from '../ogm-hub';
import { LoadingStatus } from './schema';

export function productId(product: Hub.NutritionResponse) {
	return `${product.supplierCode}|${product.productNumber}`;
}

export const productNutritionalInformationAdapter =
	createEntityAdapter<Hub.NutritionResponse>({
		selectId: productId,
	});

export const fetchNutritionalInformation = createAsyncThunk(
	'productNutritionalInformation/fetchNutritionalInformation',
	async (
		params: Hub.GetProductNutritionalInformationParams,
		{ rejectWithValue }
	) => {
		try {
			return await unwrapFetch2(
				Hub.ProductDetailsController.GetNutritionalInformation(params)
			);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface INutritionalInformationState {
	data: EntityState<Hub.NutritionResponse>;
	loadingStatus: LoadingStatus;
}

const initialState: INutritionalInformationState = {
	data: productNutritionalInformationAdapter.getInitialState(),
	loadingStatus: LoadingStatus.idle,
};

export const nutritionalInformationSlice = createSlice({
	name: 'nutritionalInformation',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchNutritionalInformation.fulfilled, (state, action) => {
			productNutritionalInformationAdapter.upsertOne(
				state.data,
				action.payload
			);
		});
		builder.addCase(fetchNutritionalInformation.rejected, (state) => {
			state.loadingStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchNutritionalInformation.pending, (state) => {
			state.loadingStatus = LoadingStatus.loading;
		});
	},
});
